import type {UUID} from 'crypto';

import type {MFAModes} from '@/enums/authentication';
import type {MessageRecordModuleType} from '@/enums/chatRecords';
import type {ContactType, ContactVisibilityStatus} from '@/enums/contact';

import type {ConnectionStatus} from '../enums/connections';

const BASE_API_URL = process.env.NEXT_PUBLIC_BASE_API_URL;

export const REGISTRATION_URLS = {
  VALIDATE_REGISTRATION: `${BASE_API_URL}/public/validate-registration`,
  MFA_AUTHENTICATION: `${BASE_API_URL}/registration/authenticate-mfa`,
  REGISTRATION_MFA_STATUS: (mfaAuthId: string) => `${BASE_API_URL}/registration/mfa-status/${mfaAuthId}`,
};

export const MFA_AUTH_URLS = {
  ALL_MFA: (module: string, includeAuthenticated = false) => `${BASE_API_URL}/auth/all-mfas/${module}/${includeAuthenticated}`,
  VALIDATE_AUTH_MODE: `${BASE_API_URL}/auth/validate-auth-mode`,
};

export const GLID_URLS = {
  GET_GLID_DETAILS: (glid: string) => `${BASE_API_URL}/platform/glid/${glid}`,
  IS_GLID_AVAILABLE: (glidID: string) => `${BASE_API_URL}/glid/check-glid-available/${glidID}`,
  GET_GLIDS: `${BASE_API_URL}/platform/glid`,
};

export const COUNTRY_URLS = {
  GET_COUNTRIES: `${BASE_API_URL}/public/countries`,
  IP_COUNTRY: `${BASE_API_URL}/public/countries/ip`,
};

export const LANGUAGE_URLS = {
  GET_LANGUAGES: `${BASE_API_URL}/languages`,
};
export const PUBLIC_PROFILE_URLS = {
  ADD_FOLLOWER: `${BASE_API_URL}/contacts/follow-public-profile`,
  GET_FOLLOWING_CONTACT_CARD_ID: (viewerId: string, followeeId: string) =>
    `${BASE_API_URL}/contacts/get-following-Contact-cardID/${viewerId}/${followeeId}`,
  GET_FOLLOWERS: (entityGUID: string) => `${BASE_API_URL}/follower?entityGUID=${entityGUID}`,
};

export const USER_CONTACTS_URLS = {
  PERSON_SHARE_SETTINGS: (userId: string) => `${BASE_API_URL}/user-contacts/default-person-share/${userId}`,
  COMPANY_SHARE_SETTINGS: (userId: string) => `${BASE_API_URL}/user-contacts/default-company-share/${userId}`,
  GET_PERSON_CONTACT_SHARE_SETTINGS: (guid: string) => `${BASE_API_URL}/user-contacts/person-contact-share-settings/${guid}`,
  GET_COMPANY_CONTACT_SHARE_SETTINGS: (guid: string) => `${BASE_API_URL}/user-contacts/company-contact-share-settings/${guid}`,
  GET_PERSON_SHARED_OUT_CONTACTS: (userId?: string, page: number = 1, pageSize: number = 3, search: string = '') =>
    `${BASE_API_URL}/user-contacts/shared-out-contacts/${userId}?page=${page}&pageSize=${pageSize}&search=${search}`,
  GET_PERSON_SHARED_OUT_CONTACT_DETAILS: (userId: string, sharedOutGuid: string) =>
    `${BASE_API_URL}/user-contacts/person-shared-out-details/${userId}?sharedOutGuid=${sharedOutGuid}`,
  GET_COMPANY_CONTACT_SHARED_OUT_SETTINGS: (guid: string) => `${BASE_API_URL}/user-contacts/company-contact-shared-out-settings/${guid}`,
  GET_PERSON_CONTACT_SHARED_OUT_SETTINGS: (guid: string) => `${BASE_API_URL}/user-contacts/person-contact-shared-out-settings/${guid}`,
  GET_SHARED_IN_CONTACT_BY_GUID: (guid: string) => `${BASE_API_URL}/user-contacts/shared-in-contact/${guid}`,
  GET_LINKED_CONTACTS: (contactGUID: string) => `${BASE_API_URL}/user-contacts/internal-linked-contacts/${contactGUID}`,
  SHARED_OUT_GUIDS_BY_CONTACT: (contactOf: string, contactGUID: string) =>
    `${BASE_API_URL}/user-contacts/shared-out-guids-by-contactGUID/${contactOf}?contactGUID=${contactGUID}`,
  SHARED_IN_CONTACT_DETAILS: (guid: string, contactGUID: string) =>
    `${BASE_API_URL}/user-contacts/shared-in-comparator-details/${guid}?contactGUID=${contactGUID}`,
  LINK_AND_AUTO_ADD_SHARED_CONTACT: (viewerId: string) => `${BASE_API_URL}/user-contacts/link-and-auto-add-shared-contact/${viewerId}`,
  GET_SHARED_OUT_CONTACTS: (entityGUID: string) => `${BASE_API_URL}/contacts/get-shared-out-records-for-entity?entityGUID=${entityGUID}`,
};

export const COMPANY_CONTACTS_URLS = {
  CHECK_ID_UNIQUENESS: (companyGUID: string, id: string, identifier: string, cardName: string, contactOf: string) =>
    `${BASE_API_URL}/company-contacts/${companyGUID}/id-uniqueness?id=${id}&identifier=${identifier}&cardName=${cardName}&contactOf=${contactOf}`,
  DEFAULT_PERSON_SHARE_OUT_SETTINGS: (orgId: string) => `${BASE_API_URL}/company-contacts/default-person-share-out-settings/${orgId}`,
  DEFAULT_COMPANY_SHARE_OUT_SETTINGS: (orgId: string) => `${BASE_API_URL}/company-contacts/default-company-share-out-settings/${orgId}`,
  SHARE_SETTINGS_FOR_PERSON_CONTACT: (orgId: string, contactGUID: string) =>
    `${BASE_API_URL}/company-contacts/person-contact-share-settings/${orgId}?guid=${contactGUID}`,
  SHARE_SETTINGS_FOR_COMPANY_CONTACT: (orgId: string, contactGUID: string) =>
    `${BASE_API_URL}/company-contacts/company-contact-share-settings/${orgId}?guid=${contactGUID}`,
  SHARE_PERSON_CONTACT: (orgId: string) => `${BASE_API_URL}/company-contacts/share-person-contact/${orgId}`,
  SHARE_COMPANY_CONTACT: (orgId: string) => `${BASE_API_URL}/company-contacts/share-company-contact/${orgId}`,
  GET_LINKED_CONTACTS: (orgId: string, contactGUID: string) =>
    `${BASE_API_URL}/company-contacts/internal-linked-contacts/${orgId}?contactGUID=${contactGUID}`,
  SHARED_OUT_GUIDS_BY_CONTACT: (contactOf: string, contactGUID: string) =>
    `${BASE_API_URL}/company-contacts/shared-out-guids-by-contactGUID/${contactOf}?contactGUID=${contactGUID}`,
  SHARED_IN_CONTACT_DETAILS: (guid: string, contactGUID: string) =>
    `${BASE_API_URL}/company-contacts/shared-in-comparator-details/${guid}?contactGUID=${contactGUID}`,
};

export const USER_URLS = {
  GET_VISIBILITY_FOR_PERSON_PROFILE: (profile: string, viewerId: string, viewAs: ContactVisibilityStatus) =>
    `${BASE_API_URL}/user-contacts/get-visibility-for-person/${profile}?viewerId=${viewerId}&viewAs=${viewAs || ''}`,
  VISIBILITY_SETTINGS: (userId: string) => `${BASE_API_URL}/user-contacts/default-visibility/${userId}`,
  UPDATE_PHONE: `${BASE_API_URL}/user/update-phoneContact`,
  VERIFY_PASSWORD: `${BASE_API_URL}/user/verify-password`,
  UPDATE_2FA: `${BASE_API_URL}/user/update-2faStatus`,
  UPDATE_TIMEOUT_OVERLAY: `${BASE_API_URL}/user/update-timeOutOverlay`,
  CHANGE_GLID: `${BASE_API_URL}/user-glid/change-glid`,
  INITIATE_GLID_CHANGE: (user: string, mode: string, glid: string) =>
    `${BASE_API_URL}/user-glid/change-glid-initiate/${user}/${mode}/${glid}`,
  ADD_MFA_TO_GLID_CHANGE: `${BASE_API_URL}/user-glid/add-mfa-to-glid-change`,
  TRIGGER_AUTH_EMAIL: (email: string, user: string) => `${BASE_API_URL}/user-auth/email-auth/${user}/${email}`,
  TRIGGER_AUTH_PHONE: (phone: string, user: string, mode: string, dialCode: string) =>
    `${BASE_API_URL}/user-auth/phone-auth/${user}/${mode}/${phone}/${dialCode}`,
  VERIFY_EMAIL: `${BASE_API_URL}/user-auth/verify-email`,
  DELETE_EMAIL: `${BASE_API_URL}/user/delete-email`,
  ADD_EMAIL: `${BASE_API_URL}/user/add-email`,
  ENABLE_MFA_EMAIL: `${BASE_API_URL}/user/enable-email-mfa`,
  MAKE_EMAIL_PRIMARY: `${BASE_API_URL}/user/make-email-primary`,
  DELETE_PHONE: `${BASE_API_URL}/user/delete-phone`,
  ADD_PHONE: (userId: string) => `${BASE_API_URL}/user/add-phone/${userId}`,
  UPDATE_PHONE_MFA: `${BASE_API_URL}/user/update-phone-mfa`,
  MAKE_PHONE_PRIMARY: `${BASE_API_URL}/user/make-phone-primary`,
  VERIFY_PHONE: `${BASE_API_URL}/user-auth/verify-phone`,
  USER_LOCALE: (id: string) => `${BASE_API_URL}/user/details/${id}`,
  UPDATE_LOCALE: (id: string, location: string) => `${BASE_API_URL}/user/update-locale/${id}/${location}`,
  SAVE_PASSWORD: `${BASE_API_URL}/user/update-password`,
  UPDATE_THEME: (id: string) => `${BASE_API_URL}/user/update-theme/${id}`,
  GET_THEME: (glid: string) => `${BASE_API_URL}/user/get-theme/${glid}`,
  GET_PREFERENCES: `${BASE_API_URL}/user/user-preference`,
  GET_LOGGED_IN_USER: `${BASE_API_URL}/user/details`,
  DELETE_PASSWORD_MFA: `${BASE_API_URL}/auth/delete-password-mfa`,
  INITIATE_DELETE_PASSWORD: (user: string, mode: string) => `${BASE_API_URL}/auth/delete-password-initiate/${user}/${mode}`,
  TRIGGER_DELETE_ACCOUNT_MFA: (user: string, mode: string) => `${BASE_API_URL}/user-auth/account-deletion/${user}/${mode}`,
  DELETE_ACCOUNT: `${BASE_API_URL}/user-auth/delete-account-trigger`,
  HAS_PASSWORD_EXPIRED: `${BASE_API_URL}/connections/has-password-expired`,
  TRIGGER_PRIMARY_ACCOUNT_MFA: (emailId: string) => `${BASE_API_URL}/user-auth/primary-email-auth/${emailId}`,
  UPDATE_PRIMARY_EMAIL: `${BASE_API_URL}/user-auth/primary-email/verify`,
  IS_GLID_MERGABLE: (glid: string) => `${BASE_API_URL}/glid/is-mergable?glid=${glid}`,
  TRIGGER_MERGE_ACCOUNT: (mode: string, glid: string) => `${BASE_API_URL}/user-auth/merge/generate-otp/${mode}/${glid}`,
  MERGE_ACCOUNT: `${BASE_API_URL}/user-auth/merge`,
  TRIGGER_DEMERGE_SETUP: `${BASE_API_URL}/registration/demerge/login-set-up`,
  DEMERGE_MFA_STATUS: (mfaId: string) => `${BASE_API_URL}/registration/demerge/mfa-status/${mfaId}`,
  DEMERGE_SETUP: `${BASE_API_URL}/registration/demerge/update-login`,
  RECOVER_ACCOUNT: `${BASE_API_URL}/user/recover-account`,
  ORG_CONNECTIONS: `${BASE_API_URL}/connections/user/active-org-connections`,
  UPDATE_LANDING_PAGE_PREFERENCE: `${BASE_API_URL}/user/update-landing-page-preference`,
  GET_PREFERENCE: `${BASE_API_URL}/user/get-user-preference`,
  SHARE_CONTACT: `${BASE_API_URL}/contacts/share-person-contact`,
  SAVE_MATCH_SETTINGS: `${BASE_API_URL}/user/match-settings`,
  GET_DEFAULT_SHARE_SETTINGS: `${BASE_API_URL}/user/get-default-share-settings`,
  UPDATE_DEFAULT_SHARE_SETTINGS: `${BASE_API_URL}/user/update-default-share-settings`,
  UPDATE_DEFAULT_CONFLICT_SETTINGS: (entityGUID) => `${BASE_API_URL}/user/update-default-conflict-settings/${entityGUID}`,
  GET_CONNECTION_STATUS_COUNT: `${BASE_API_URL}/connections/user/connection-status-count`,
};

export const SECURITY_POLICY_URLS = {
  PLATFORM_PASSWORD_POLICY: `${BASE_API_URL}/securityPolicy/get-platform-password-policy`,
  VALIDATE_PRELAUNCH: `${BASE_API_URL}/securityPolicy/validate-prelaunch-password`,
  PRELAUNCH_STATUS: `${BASE_API_URL}/securityPolicy/get-prelaunch-status`,
  GLOBAL_REGISTRATION: `${BASE_API_URL}/securityPolicy/get-global-registration-status`,
  USER_PASSWORD_POLICY: () => `${BASE_API_URL}/connections/get-user-password-policy`,
};

export const PLATFORM_SECURITY_POLICY_URLS = {
  SECURITY_POLICY: `${BASE_API_URL}/platform/security-policy`,
  UPDATE_PLATFORM_SECURITY_POLICY: `${BASE_API_URL}/platform/security-policy/update-platform-security-policy`,
};
export const ORGS = {
  VISIBILITY_SETTINGS: (userId: string) => `${BASE_API_URL}/company-contacts/org-visibility/${userId}`,
  GET_VISIBILITY_FOR_COMPANY_PROFILE: (profile: string, viewerId: string, viewAs: ContactVisibilityStatus) =>
    `${BASE_API_URL}/company-contacts/get-visibility-for-company/${profile}?viewerId=${viewerId}&viewAs=${viewAs || ''}`,
  GET_USER_ORG: () => `${BASE_API_URL}/organization/user-orgs`,
  RECOVER_ORG: (companyId: string) => `${BASE_API_URL}/organization/${companyId}/recovery`,
  CREATE_COMPANY: `${BASE_API_URL}/connections/org/create`,
  GET_ORG_BY_ID: (companyId: string) => `${BASE_API_URL}/organization/${companyId}`,
  GET_ORG_COMMUNITY_STATUS: (companyId: string) => `${BASE_API_URL}/organization/org-community-status/${companyId}`,
  TRIGGER_DELETE_ORG: (companyId: string, mode: string) => `${BASE_API_URL}/organization/${companyId}/account-deletion/${mode}`,
  DELETE_ORG: `${BASE_API_URL}/public/organization/delete`,
  CUSTOMER_ID_UNIQUENESS: (companyGUID: string, customerId: string) =>
    `${BASE_API_URL}/org-contacts/${companyGUID}/customer-id-uniqueness?id=${customerId}`,
  COMPANY_SECURITY_POLICY: (id: string) => `${BASE_API_URL}/organization/${id}/security-policy`,
  GET_DEFAULT_SHARE_SETTINGS: `${BASE_API_URL}/organization/get-default-share-settings`,
  UPDATE_DEFAULT_SHARE_SETTINGS: `${BASE_API_URL}/organization/update-default-share-settings`,
  UPDATE_DEFAULT_CONFLICT_SETTINGS: (entityGUID) => `${BASE_API_URL}/organization/update-default-conflict-settings/${entityGUID}`,
};

export const REGISTERED_ORGS_URLS = {
  GET_ALL_ORGS: `${BASE_API_URL}/registered-orgs`,
  GET_ORG: (id: string) => `${BASE_API_URL}/registered-orgs/${id}`,
  UPDATE_ACCOUNT_PRIVILEGES: (guid: string) => `${BASE_API_URL}/registered-orgs/update-account-privileges/${guid}`,
  UPDATE_COUNTRY: (guid: string) => `${BASE_API_URL}/registered-orgs/update-country/${guid}`,
  DELETE_ORGANIZATION: (guid: string) => `${BASE_API_URL}/registered-orgs/delete-organization/${guid}`,
  RECOVER_ORGANIZATION: (guid: string) => `${BASE_API_URL}/registered-orgs/recover-organization/${guid}`,
};

export const AGILE_ONE = {
  MARKETING_WEBSITE: 'https://agile-one.com/',
};

export const IP_CAPTCHA = {
  CHECK_IS_IP_ALLOWED: `${BASE_API_URL}/public/check-is-ip-allowed`,
};
export const TRANSLATIONS_URLS = {
  GET_TRANSLATIONS: (language: string, namespace: string) =>
    `${BASE_API_URL}/translations/${language}?module=${namespace === 'translation' ? 'common' : namespace}`,
};

export const REGISTERED_USERS_URLS = {
  GET_COMPANY_CONNECTIONS: (userId: string) => `${BASE_API_URL}/registered-users/${userId}/connections`,
  UPDATE_USER_SETTINGS: `${BASE_API_URL}/registered-users/update-settings`,
  GET_USER_DETAILS: (userId: string) => `${BASE_API_URL}/registered-users/${userId}`,
  SEARCH_USERS: (text: string) => `${BASE_API_URL}/registered-users?searchText=${text}`,
  GET_REGISTERED_USERS: `${BASE_API_URL}/registered-users`,
  DELETE_USER: (userId: string) => `${BASE_API_URL}/registered-users/${userId}`,
  GET_USER_SESSIONS_PLATFORM: (userId: string) => `${BASE_API_URL}/platform/session/user/${userId}`,
  RECOVER_USER_ACCOUNT: (userId: string) => `${BASE_API_URL}/registered-users/recover-account/${userId}`,
};
export const LOGIN_URLS = {
  CREATE_SESSION: () => `${BASE_API_URL}/public/create-session`,
  CHECK_GLID: (glid: string) => `${BASE_API_URL}/public/is-glid-valid/${glid}`,
  PASSWORD_LOGIN: () => `${BASE_API_URL}/auth/login`,
  LOGIN_WITH_MFA_OTP: () => `${BASE_API_URL}/auth/login-with-mfa-otp`,
  VERIFY_LOGIN_AUTH_REF: () => `${BASE_API_URL}/auth/verify-login-auth-ref`,
  VERIFY_LOGIN_MAGIC_LINK: () => `${BASE_API_URL}/auth/verify-login-magic-link`,
  CHECK_SESSION_STATE: () => `${BASE_API_URL}/auth/check-session-state`,
  SEND_MFA_OTP: (glid: string, mode: MFAModes) => `${BASE_API_URL}/public/generate-otp/${glid}?type=${mode}`,
  RECOVER_GLID: () => `${BASE_API_URL}/public/forgot-glid`,
  FORGOT_PASSWORD_MFA: () => `${BASE_API_URL}/public/send-forgot-password-mfa`,
  VALIDATE_FORGOT_PASSWORD_MFA: () => `${BASE_API_URL}/public/validate-forgot-password-mfa`,
  RESET_PASSWORD: () => `${BASE_API_URL}/auth/reset-password`,
  DELETE_PASSWORD: () => `${BASE_API_URL}/auth/delete-password`,
  GET_VERIFIED_MFAS: (glid: string) => `${BASE_API_URL}/public/get-verified-mfas/${glid}`,
  CHECK_GLID_HAS_PASSWORD: (glid: string) => `${BASE_API_URL}/public/glid-has-password-flow/${glid}`,
  GET_MY_SESSION: () => `${BASE_API_URL}/auth/get-my-session`,
  COMPRISED_PASSWORD: (password: string) => `${BASE_API_URL}/auth/is-password-compromised?password=${password}`,
};

export const SESSIONS = {
  GET_SESSIONS_PLATFORM: () => `${BASE_API_URL}/platform/session/get`,
  GET_USER_SESSIONS_PLATFORM: (userId: string) => `${BASE_API_URL}/platform/session/user/${userId}`,
  GET_SESSION: (sessionId: string) => `${BASE_API_URL}/sessions/get/${sessionId}`,
  LOG_OUT: () => `${BASE_API_URL}/auth/logout`,
  CLOSE_SESSION: () => `${BASE_API_URL}/auth/close-session`,
  CLOSE_ALL_SESSIONS: () => `${BASE_API_URL}/auth/close-all-sessions`,
  EXPIRED_MFAS: (sessionId: string) => `${BASE_API_URL}/auth/expired-mfas/${sessionId}`,
  GET_USER_SESSIONS: () => `${BASE_API_URL}/sessions/get`,
  GET_SESSION_MFA_EXPIRY: (sessionId: string) => `${BASE_API_URL}/sessions/session-mfa-expiry/${sessionId}`,
  GET_SESSION_METADATA: (sessionId: string) => `${BASE_API_URL}/public/session-metadata/${sessionId}`,
};

export const IPCaptcha_URLS = {
  GET_IP_ADDRESSES: `${BASE_API_URL}/platform-testing/get-ip-addresses`,
  EDIT_IP_ADDRESS: (userId: string) => `${BASE_API_URL}/platform-testing/edit-ip-address/${userId}`,
  ADD_IP_ADDRESS: `${BASE_API_URL}/platform-testing/add-ip-address`,
  SEARCH_IP: (text: string) => `${BASE_API_URL}/platform-testing/get-ip-addresses?searchText=${text}`,
};

export const PhoneNumberAlias_URLS = {
  GET_PHONE_NUMBER_ALIAS_BY_ID: (userId: string, guid: string) =>
    `${BASE_API_URL}/platform-testing/get-phone-number-alias/${userId}?guid=${guid}`,
  GET_ALL_PHONE_NUMBER_ALIASES: `${BASE_API_URL}/platform-testing/get-phone-number-aliases`,
  ADD_PHONE_NUMBER_ALIAS: `${BASE_API_URL}/platform-testing/add-phone-number-alias`,
  EDIT_PHONE_NUMBER_ALIAS: (userId: string) => `${BASE_API_URL}/platform-testing/update-phone-number-alias/${userId}`,
  IS_VALUE_UNIQUE: (guid: string, fieldName: string, value: string) =>
    `${BASE_API_URL}/platform-testing/is-value-unique?guid=${guid}&fieldName=${fieldName}&value=${value}`,
  SEARCH_PHONE_NUMBER_ALIAS: (searchText: string) => `${BASE_API_URL}/platform-testing/get-phone-number-aliases?searchText=${searchText}`,
};

export const DOMAIN_CONTROL = {
  GET: (page: number, pageSize: number, search: string) =>
    `${BASE_API_URL}/platform/domain-control?page=${page}&pageSize=${pageSize}&search=${search}`,
  GET_BY_ID: (id: string) => `${BASE_API_URL}/platform/domain-control/${id}`,
  GET_TOP_DOMAIN_BY_ID: (id: string) => `${BASE_API_URL}/platform/domain-control/get-top-domain/${id}`,
  GET_CHILDREN_DOMAINS_BY_ID: (id: string) => `${BASE_API_URL}/platform/domain-control/get-children/${id}`,
  GET_SUB_DOMAINS_BY_ID: (id: string) => `${BASE_API_URL}/platform/domain-control/get-sub-domains/${id}`,
  POST: () => `${BASE_API_URL}/platform/domain-control`,
  PATCH: () => `${BASE_API_URL}/platform/domain-control`,
  GET_BAN_STATUS_BY_ID: (id: string) => `${BASE_API_URL}/platform/domain-control/get-ban-status/${id}`,
  GET_EMAIL_BAN_STATUS: (email: string) => `${BASE_API_URL}/public/check-domain-status?email=${email}`,
  GET_DOMAINS_STATUS: `${BASE_API_URL}/public/domains-status`,
};

export const CONNECTION_URLS = {
  GET_CONNECTION: (conId: string) => `${BASE_API_URL}/connections/${conId}`,
  GET_B2B_CONNECTION: (conId: string) => `${BASE_API_URL}/connections/b2b/${conId}`,
  ACCEPT_INVITE_CODE: `${BASE_API_URL}/connections/user/accept-invite-code`,
  GET_INVITE_CODE: (guid: string) => `${BASE_API_URL}/connections/user/get-invite-code?guid=${guid}`,
  GET_INVITES_COUNT: `${BASE_API_URL}/connections/user/invites/count`,
  GET_USER_INVITES: (status: string) => `${BASE_API_URL}/connections/user/invites?status=${status}`,
  GET_CONTACT_CONNECTION: `${BASE_API_URL}/connections/get-connection-profile-for-contact`,
  ACCEPT_DIRECT_INVITE: `${BASE_API_URL}/connections/user/accept-direct-invite`,
  CREATE_CONTACT: `${BASE_API_URL}/connections/create/contact-and-connection`,
  GET_USER_ORG_CONNECTIONS: (userId: string) => `${BASE_API_URL}/connections/user/${userId}/org-connections`,
  GET_ORG_USER_CONNECTIONS: (orgId: string) => `${BASE_API_URL}/connections/org/${orgId}/user-connections`,
  CREATE_PROFILE: `${BASE_API_URL}/connections/create`,
  SEND_INVITE_CODE: (orgId: string) => `${BASE_API_URL}/connections/org/${orgId}/generate-invite-code`,
  GET_ACTIVE_INVITE_CODE: (orgId: string, contactId: string) =>
    `${BASE_API_URL}/connections/org/${orgId}/invite-code?contactId=${contactId}`,
  SEND_DIRECT_INVITE: `${BASE_API_URL}/connections/org/send-direct-invite`,
  CONFIRM_CONNECTION_ORG: (orgId: string) => `${BASE_API_URL}/connections/org/${orgId}/connection-respond`,
  GET_B2B_CONNECTION_DETAILS: (guid: string) => `${BASE_API_URL}/connections/get-b2b-connection-details/${guid}`,
  DISABLE_CONNECTION: `${BASE_API_URL}/connections/disable`,
  ENABLE_CONNECTION: `${BASE_API_URL}/connections/enable`,
  GET_SECURITY_POLICY: (connId: string) => `${BASE_API_URL}/connections/${connId}/security-policy`,
  GET_SESSIONS: (userId: string) => `${BASE_API_URL}/sessions/user/${userId}`,
  SEND_B2B_INVITE: `${BASE_API_URL}/connections/b2b/send-invite`,
  GET_B2B_CONNECTIONS: (orgId: string) => `${BASE_API_URL}/connections/b2b/org/${orgId}/b2b-connections`,
  UPDATE_B2B_CONNECTIONS: `${BASE_API_URL}/connections/b2b/update-connection`,
  CREATE_B2B_PROFILE: `${BASE_API_URL}/connections/b2b`,
  GET_CONNECTION_STATUS: (fromGUID: string, toGUID: string) => `${BASE_API_URL}/connections/get-connection-status/${fromGUID}/${toGUID}`,
  GET_RECOMMENDABLE_CONTACTS: (orgId: string) => `${BASE_API_URL}/connections/b2b/recommendable-contacts/${orgId}`,
  GET_SENDABLE_CONTACTS: (orgId: string, contactGUID: string) =>
    `${BASE_API_URL}/connections/b2b/recommendation-sendable-contacts/${orgId}/${contactGUID}`,
  SEND_RECOMMENDATIONS: `${BASE_API_URL}/connections/b2b/send-recommendations`,
  SENT_RECOMMENDATIONS: `${BASE_API_URL}/connections/b2b/recommendations`,
  UPDATE_COMPANY_SECURITY_POLICY: (id: string) => `${BASE_API_URL}/connections/${id}/company-security-policy`,
  GET_ORG_USER_CONNECTIONS_COUNT: (entityId: string, status: ConnectionStatus) =>
    `${BASE_API_URL}/connections/org/get-connections-count/${entityId}/${status}`,
};

export const CONTACT_URLS = {
  ADD_CONTACT: `${BASE_API_URL}/contacts/add-contact`,
  UPDATE_PERSON_CONTACT_BY_ID: (id: string) => `${BASE_API_URL}/user-contacts/${id}`,
  CONTACT_BY_ID: (id: string) => `${BASE_API_URL}/contacts/get-contact-info/${id}`,
  GET_CONNECTED_CONTACTS_BY_ID: (id: string) => `${BASE_API_URL}/contacts/get-connected-contacts/${id}`,
  UPDATE_COMPANY_CONTACT: (id: string) => `${BASE_API_URL}/user-contacts/company-contact/${id}`,
  GET_COMPANY_CONTACT_QUICK_FILTER: (id: string, contactOf: string) =>
    `${BASE_API_URL}/contacts/get-quick-filter-contacts/${id}/${contactOf}`,
  GET_INTERNAL_CONTACT_DIRECTORY: (contactOf: string) => `${BASE_API_URL}/contacts/get-internal-contacts-lite/${contactOf}`,
  GET_CONTACT_BY_PROFILE: (id: string, viewerId: string) => `${BASE_API_URL}/contacts/contact-by-profileLink/${id}/${viewerId}`,
  GET_COMPANY_CONTACTS_METADATA: `${BASE_API_URL}/contacts/company-contacts-metadata`,
  SHARE_CONTACT: `${BASE_API_URL}/contacts/share-company-contact`,
  GET_COMPANY_CONTACTS_LOOKUP: (orgId: string, type: ContactType) => `${BASE_API_URL}/contacts/org/${orgId}/look-up?type=${type}`,
  MERGE_CONTACT: `${BASE_API_URL}/contacts/merge-contact`,
  DEMERGE_CONTACT: `${BASE_API_URL}/contacts/demerge-contact`,
  GET_CONTACT_ID_BY_PROFILE_LINK_GUID: (profileLinkGUID: string) =>
    `${BASE_API_URL}/contacts/get-contactGUID-by-profileLinkGUID/${profileLinkGUID}`,
  MATCH_CONTACTS: `${BASE_API_URL}/contacts/match-contacts`,
  CREATE_INTERNAL_CONTACT_FROM_SHARED_IN: (viewerId: string) =>
    `${BASE_API_URL}/contacts/create-internal-contact-from-shared-in/${viewerId}`,
  GET_SHARED_IN_CONTACTS: `${BASE_API_URL}/contacts/shared-in-contacts`,
  GET_SHARED_IN_CONTACT_FOR_CONNECTION: `${BASE_API_URL}/contacts/shared-in-contact-for-connection`,
  GET_SHARED_IN_CONTACTS_FOR_MATCH: (contactOf: string, contactType?: string) =>
    `${BASE_API_URL}/contacts/shared-in-contacts-for-match/${contactOf}${contactType ? `?contactType=${contactType}` : ''}`,
  GET_SHARED_IN_CONTACT_BY_ID: (id: string) => `${BASE_API_URL}/contacts/get-shared-in-contact-by-id/${id}`,
  GET_INTERNAL_CONTACTS_FOR_MATCH: (contactOf: string, contactType?: string) =>
    `${BASE_API_URL}/contacts/internal-contacts-for-match/${contactOf}${contactType ? `?contactType=${contactType}` : ''}`,
  CHECK_PROFILE_AVAILABILITY: (profileLink: string, guid: string) =>
    `${BASE_API_URL}/contacts/check-profileLinkStatus/${profileLink}/${guid}`,
  SAVE_PUBLIC_PROFILE: (guid: string) => `${BASE_API_URL}/contacts/save-public-profile/${guid}`,
  SAVE_COMPARATOR_CHANGES: `${BASE_API_URL}/contacts/save-comparator-changes`,
  CREATE_CONTACT_IF_ELIGIBLE_FOR_SHARED_IN: (contactOf: string, sharedInContactId: string) =>
    `${BASE_API_URL}/contacts/create-match-contact-of-eligible/${contactOf}/${sharedInContactId}`,
  UNLINK_SHARED_IN_CONTACT: (sharedInContactId: string) => `${BASE_API_URL}/contacts/unlink-shared-in-contact/${sharedInContactId}`,
  GET_PREDICTED_CONTACT: (contactOf: string, contactId: string) =>
    `${BASE_API_URL}/contacts/predict-contact-for-match/${contactOf}/${contactId}`,
  GET_SHARED_OUT_SETTINGS_SHARED_TO: (sharedFromGUID: string, contactGUID: string) =>
    `${BASE_API_URL}/contacts/shared-out-settings/${sharedFromGUID}?contactGUID=${contactGUID}`,
  UPDATE_SHARED_OUT_SETTINGS: (guid: string) => `${BASE_API_URL}/contacts/update-shared-out-settings/${guid}`,
  GET_ID_ERRORS_FOR_COMPANY_CARD: (contactOf: string) => `${BASE_API_URL}/contacts/get-Id-Errors-For-Company-Card/${contactOf}`,
  IGNORE_SHARED_IN_CONTACT: (shareInGUID: string) => `${BASE_API_URL}/contacts/${shareInGUID}/ignore-shared-in`,
  GET_PROFILE_LINK_BY_GUID: (ContactType: ContactType, guid: string) =>
    `${BASE_API_URL}/contacts/profile-link-by-guid/${ContactType}/${guid}`,
  GET_CONTACT_CARD_INFO: (guid: string, contactOf: string) => `${BASE_API_URL}/contacts/contact-card-details/${guid}/${contactOf}`,
  GET_SHARED_CONTACTS_BY_GUIDS: `${BASE_API_URL}/contacts/get-shared-in-contacts`,
  GET_SELF_CONTACT_BY_PROFILE_LINK_GUID: (guid: UUID) => `${BASE_API_URL}/contacts/get-self-contact-by-profileLinkGUID/${guid}`,
  GET_MATCH_RECORDS: (contactOf: string, sharedInContactGUID: string) =>
    `${BASE_API_URL}/contacts/get-match-records/${contactOf}/${sharedInContactGUID}`,
  IGNORE_SHARED_IN_UPDATES: (sharedInContactGUID: string) => `${BASE_API_URL}/contacts/shared-in-contacts/${sharedInContactGUID}`,
  GET_CONTACT_BY_PROFILE_LINK_GUID: (contactOfGUID: UUID, guid: UUID) =>
    `${BASE_API_URL}/contacts/get-contact-by-profileLinkGUID/${contactOfGUID}/${guid}`,
  GET_SHARED_RECORDS_BY_RECORD_GUID: `${BASE_API_URL}/contacts/record-family`,
  GET_SITE_LOCATIONS: (entityGUID: string) => `${BASE_API_URL}/contacts/site-locations/${entityGUID}`,
  GET_SITE_CONTACT_DETAILS: (contactGUID: string, siteGUID: string, recordOfGUID: string) =>
    `${BASE_API_URL}/contacts/site-contact/${contactGUID}/${siteGUID}/${recordOfGUID}`,

  GET_CONTACTS_BY_PROFILE_LINK_GUIDS: (contactOf: string) => `${BASE_API_URL}/contacts/get-contacts-by-profile-link-guids/${contactOf}`,
  DELETE_SITE_CONTACT: (contactGUID: string, siteGUID: string) => `${BASE_API_URL}/contacts/${contactGUID}/site/${siteGUID}`,
  CREATE_SITE_CONTACT: `${BASE_API_URL}/contacts/create-site-contact`,
};

export const FILES_URLS = {
  ACCEPT_SHARE: `${BASE_API_URL}/files/shared-in/accept`,
  IGNORE_SHARE: `${BASE_API_URL}/files/shared-in/ignore`,
  HEED_SHARE: `${BASE_API_URL}/files/shared-in/heed`,
  TRASH_ACCEPT_SHARE: (trashRecordGUID: UUID) => `${BASE_API_URL}/files/archive/shared-in/accept/${trashRecordGUID}`,
  TRASH_IGNORE_SHARE: (trashRecordGUID: UUID) => `${BASE_API_URL}/files/archive/shared-in/ignore/${trashRecordGUID}`,
  TRASH_HEED_SHARE: (trashRecordGUID: UUID) => `${BASE_API_URL}/files/archive/shared-in/heed/${trashRecordGUID}`,
  GET_FILES_DIRECTORY: `${BASE_API_URL}/files/directory/fetch-file-record-list-paginate`,
  GET_FILE_RECORD_BY_ID: `${BASE_API_URL}/files/directory/get-file-record-by-id`,
  GET_FILE_RECORDS_BY_IDS: `${BASE_API_URL}/files/directory/fetch-files-by-ids`,
  UPDATE_FILE_RECORD_BY_ID: (fileGUID: string) => `${BASE_API_URL}/files/directory/update-file-by-guid/${fileGUID}`,
  GET_FILE_METADATA_DIRECTORY: `${BASE_API_URL}/files/meta-data/fetch-file-metadata-record-lite-paginate`,
  GET_FILE_METADATA_RECORD_BY_ID: `${BASE_API_URL}/files/meta-data/get-metadata-record-by-id`,
  GET_SHARED_IN_RECORDS: `${BASE_API_URL}/files/shared-in/fetch-sharedIn-record-lite-paginate`,
  GET_SHARED_IN_RECORD_BY_ID: (sharedInGUID: string) => `${BASE_API_URL}/files/shared-in/record-by-id/${sharedInGUID}`,
  GET_SHARED_OUT_RECORDS: `${BASE_API_URL}/files/shared-out/fetch-sharedOut-record-lite-paginate`,
  GET_SHARED_OUT_RECORD_BY_ID: (sharedOutGUID: string) => `${BASE_API_URL}/files/shared-out/get-sharedOut-record-by-id/${sharedOutGUID}`,
  GET_ALL_TRASHED_RECORDS: `${BASE_API_URL}/files/archive/fetch-archived-list-paginate`,
  GET_TRASHED_RECORD_BY_GUID: (archiveRecordGUID: string) => `${BASE_API_URL}/files/archive/get-archived-record-by-id/${archiveRecordGUID}`,
  GET_DELETED_RECORDS: `${BASE_API_URL}/files/archive/fetch-deleted-records-list-paginate`,
  GET_DELETED_RECORD_BY_GUID: (archiveRecordGUID: string) => `${BASE_API_URL}/files/archive/get-deleted-record-by-id/${archiveRecordGUID}`,
  DELETE_FILE: `${BASE_API_URL}/files/directory/delete`,
  DELETE_SHARED_OUT_FILE: `${BASE_API_URL}/files/shared-out/delete`,
  DELETE_SHARED_IN_FILE: `${BASE_API_URL}/files/shared-in/delete`,
  DELETE_FILE_METADATA: `${BASE_API_URL}/files/meta-data/delete`,
  MOVE_FILES_TO_TRASH: `${BASE_API_URL}/files/directory/move-files-to-trash`,
  DOWNLOAD_FILE_BY_GUID: (guid: string) => `${BASE_API_URL}/files/directory/download-file-by-guid/${guid}`,
  DOWNLOAD_SHARED_IN_FILE_BY_GUID: (guid: string) => `${BASE_API_URL}/files/shared-in/download-file-by-guid/${guid}`,
  VIEW_FILE_BY_GUID: (guid: string) => `${BASE_API_URL}/files/directory/view-file-by-guid/${guid}`,
  VIEW_SHARED_IN_FILE_BY_GUID: (guid: string) => `${BASE_API_URL}/files/shared-in/view-file-by-guid/${guid}`,
  RECOVER_FILE_BY_GUID: (guid: string) => `${BASE_API_URL}/files/archive/recover-archived-record/${guid}`,
  UPLOAD_FILE: `${BASE_API_URL}/files/directory/process-file-upload`,
  CHECK_FOR_DUPLICATE: `${BASE_API_URL}/files/directory/check-for-duplicate`,
  CHECK_IS_FILE_SCAN_REQUIRED: (fileGUID: string) => `${BASE_API_URL}/files/directory/check-if-scan-required/${fileGUID}`,
  CHECK_IS_FILE_SCAN_REQUIRED_FOR_SHARED_IN: (sharedInGUID: string) =>
    `${BASE_API_URL}/files/shared-in/check-if-scan-required/${sharedInGUID}`,
  GET_MALWARE_HISTORY: (fileGUID: string) => `${BASE_API_URL}/files/directory/malware-history/${fileGUID}`,
  MOVE_FILES_TO_DIRECTORY: `${BASE_API_URL}/files/directory/move-files-to-directory`,
  GET_SHARE_SETTINGS: `${BASE_API_URL}/files/share/get-settings`,
  GET_MULTIPLE_SETTINGS: `${BASE_API_URL}/files/share/get-multiple-settings`,
  UPDATE_SHARE_SETTINGS: `${BASE_API_URL}/files/share/update-settings`,
  UPDATE_MULTIPLE_SHARE_SETTINGS: `${BASE_API_URL}/files/share/multishare-update-settings`,
  SHARE_RECORD: `${BASE_API_URL}/files/share`,
  MULTIPLE_SHARE_RECORD: `${BASE_API_URL}/files/share/multiple-share`,
  GET_ASSOCIATED_RECORDS: `${BASE_API_URL}/files/share/get-sharefields-with-associated-records`,
  GET_ASSOCITED_RECORDS_FOR_MULTIPLE_FILES: `${BASE_API_URL}/files/share/get-sharefields-with-associated-records-for-multiple-files`,
  GET_SHARED_OUT_COMPARATOR_DATA: `${BASE_API_URL}/files/shared-out/comparator`,
  GET_SHARED_IN_COMPARATOR_DATA: `${BASE_API_URL}/files/shared-in/comparator`,

  SAVE_SHARED_IN_COMPARATOR_DATA: `${BASE_API_URL}/files/shared-in/save-comparator`,
  SAVE_SHARED_OUT_COMPARATOR_DATA: `${BASE_API_URL}/files/shared-out/save-comparator`,

  GET_SHARED_RECORDS_BY_RECORD_GUID: `${BASE_API_URL}/files/share/record-family`,
};

export const VALIDATION_URLS = {
  CREATE_ORG_VALIDATOR: `${BASE_API_URL}/identityValidators/create-org-validator`,
  SELF_VALIDATED_STATUS: (fromGUID: string, toGUID: string) =>
    `${BASE_API_URL}/identityValidators/get-validator-status/${fromGUID}/${toGUID}`,
  GET_ORG_VALIDATORS: (guid: string) => `${BASE_API_URL}/identityValidators/get-validators/${guid}`,
  GET_VIEWER_TYPE: (guid: string) => `${BASE_API_URL}/identityValidators/viewer-type/${guid}`,
};

export const EVENTS_URLS = {
  INTERVIEWS: `${BASE_API_URL}/calendar-events/interviews`,
  GET_INTERVIEWS: (eventId: string) => `${BASE_API_URL}/calendar-events/interviews/${eventId}`,
  DELETE_INTERVIEW: (eventId: string) => `${BASE_API_URL}/calendar-events/participants/events/${eventId}`,
  CANCEL_INTERVIEW: (eventId: string) => `${BASE_API_URL}/calendar-events/participants/events/${eventId}/cancel`,
  GET_TRASH_LIST: (entityId: string) => `${BASE_API_URL}/calendar-events/trash/${entityId}`,
  GET_DELETED_LIST: (guid: string) => `${BASE_API_URL}/calendar-events/trash/delete-list/${guid}`,
  GET_SHARED_OUT: (guid: string) => `${BASE_API_URL}/calendar-events/shared-out/${guid}`,
  GET_SHARED_IN: (guid: string) => `${BASE_API_URL}/calendar-events/shared-in/${guid}`,
  GET_SHARED_IN_DETAILS: (sharedRecordGUID: string) => `${BASE_API_URL}/calendar-events/shared-in/event/${sharedRecordGUID}`,
  GET_SHARED_OUT_DETAILS: (sharedRecordGUID: string) => `${BASE_API_URL}/calendar-events/shared-out/event/${sharedRecordGUID}`,
  GET_SHARED_IN_COMPARATOR_DATA: (sharedRecordGUID: string) =>
    `${BASE_API_URL}/calendar-events/shared-in/event/comparator/${sharedRecordGUID}`,
  GET_SHARED_OUT_COMPARATOR_DATA: (sharedRecordGUID: string) =>
    `${BASE_API_URL}/calendar-events/shared-out/event/comparator/${sharedRecordGUID}`,
  GET_DELETE_RECORD_BY_GUID: (guid: string) => `${BASE_API_URL}/calendar-events/trash/deleted/${guid}`,
  TRASH_RECORD_BY_GUID: (guid: string) => `${BASE_API_URL}/calendar-events/trash/${guid}`,
  DELETE_SHARED_IN: (guid: string) => `${BASE_API_URL}/calendar-events/trash/shared-in/${guid}`,
  DELETE_SHARED_OUT: (guid: string) => `${BASE_API_URL}/calendar-events/trash/shared-out/${guid}`,
  RECOVER_TRASH_RECORD: (guid: string) => `${BASE_API_URL}/calendar-events/participants/recover/${guid}`,
  CHECK_EVENT_IS_TRASH_OR_NOT: (eventId: string) => `${BASE_API_URL}/calendar-events/trash/event-status/${eventId}`,
  GET_SHARED_OUT_LIST: (guid: string) => `${BASE_API_URL}/calendar-events/shared-out/${guid}`,
  GET_TRASHED_EVENT_BY_EVENT_GUID: (eventGUID: string) => `${BASE_API_URL}/calendar-events/trash/trash-record-by-event-guid/${eventGUID}`,
  GET_SHARED_RECORDS_BY_RECORD_GUID: (recordGUID: string) => `${BASE_API_URL}/calendar-events/share/record-family/${recordGUID}`,
};

export const VIEWERS_URLS = {
  CREATE_VIEWER: `${BASE_API_URL}/viewers/create-viewer`,
  GET_VIEWS: `${BASE_API_URL}/viewers/get-my-views`,
  GET_VIEWERS: `${BASE_API_URL}/viewers/get-my-viewers`,
  GET_VIEWERS_BY_VIEWERGUID: `${BASE_API_URL}/viewers/get-viewers-by-viewerGUID`,
};

export const PARTICIPANT_URLS = {
  PARTICIPANT: `${BASE_API_URL}/calendar-events/participants`,
  GET_PARTICIPANTS: (entityId: string) => `${BASE_API_URL}/calendar-events/participants/${entityId}`,
  PARTICIPANT_BY_GUID: (participantGUID: string) => `${BASE_API_URL}/calendar-events/participants/${participantGUID}`,
  DELETE_PARTICIPANT_BY_GUID: (participantGUID: string, recordOfGUID: string, isResetSyncSettings: boolean) =>
    `${BASE_API_URL}/calendar-events/participants/${participantGUID}?recordOfGUID=${recordOfGUID}&isResetSyncSettings=${isResetSyncSettings}`,

  GET_PARTICIPANTS_BY_EVENT_ID: (eventId: string) => `${BASE_API_URL}/calendar-events/participants/event/${eventId}`,
  GET_CONTEXT_OPTIONS: (contactGUID: string) => `${BASE_API_URL}/calendar-events/participants/context-options/${contactGUID}`,
  EVENT_FOLLOWER: `${BASE_API_URL}/calendar-events/followers`,
  CHECK_DUPLICATE_PARTICIPANT: (contactGUID: string, eventGUID: string) =>
    `${BASE_API_URL}/calendar-events/participants/is-duplicate-participant/${contactGUID}?eventGUID=${eventGUID}`,
  CREATE_PARTICIPANTS: (eventRecordGUID: string) => `${BASE_API_URL}/calendar-events/participants/create/${eventRecordGUID}`,
  EDIT_PARTICIPANTS: (eventRecordGUID: string) => `${BASE_API_URL}/calendar-events/participants/edit/${eventRecordGUID}`,
  ENTITY_PARTICIPANT_RECORDS: (recordOfGUID: string) =>
    `${BASE_API_URL}/calendar-events/participants/entity-participant-records/${recordOfGUID}`,
  GET_PARTICIPANT_ROLE: (contactGUID: string, eventRecordGUID: string) =>
    `${BASE_API_URL}/calendar-events/participants/role/${eventRecordGUID}?contactGUID=${contactGUID}`,
};

export const FOLLOWER_URLS = {
  GET_FOLLOWERS: (entityId: string) => `${BASE_API_URL}/calendar-events/participants/followers/${entityId}`,
  GET_FOLLOWER: (guid: string) => `${BASE_API_URL}/calendar-events/participants/followers/${guid}`,
  DELETE_FOLLOWER: (recordGUID: string) => `${BASE_API_URL}/calendar-events/followers/${recordGUID}`,
  EVENT_FOLLOWERS: (eventId: string) => `${BASE_API_URL}/calendar-events/followers/${eventId}`,
  ENTITY_FOLLOWER_RECORDS: (recordOfGUID: string) => `${BASE_API_URL}/calendar-events/followers/entity-follower-records/${recordOfGUID}`,
};

export const CALENDAR_VISUALIZATION_URLS = {
  GET_CALENDAR_CONTEXT_ORDERS_BY_ENTITY_ID: (entityId: string) => `${BASE_API_URL}/calendar-visualization/${entityId}`,
  UPDATE_CALENDAR_CONTEXT_ORDERS_BY_ENTITY_ID: `${BASE_API_URL}/calendar-visualization/update-calendar-context-orders`,
  GET_YEAR_WISE_SCHEDULED_EVENT_DETAILS: `${BASE_API_URL}/calendar-visualization/list-view-events/scheduled`,
  GET_UNSCHEDULED_EVENT_DETAILS: `${BASE_API_URL}/calendar-visualization/list-view-events/unscheduled`,
  UPDATE_CALENDAR_VISUALIZATION_SETTINGS: (entityId: string) => `${BASE_API_URL}/calendar-visualization/${entityId}/update-preferences`,
  UPDATE_CALENDAR_ORDERS: (entityGUID: string) => `${BASE_API_URL}/calendar-visualization/${entityGUID}/order`,
  GET_EVENT_DETAILS: (eventId: string) => `${BASE_API_URL}/calendar-visualization/events/${eventId}`,
  GET_EVENTS_FOR_WEEK_MONTH_VIEW: (entityId: string) => `${BASE_API_URL}/calendar-visualization/fetch-week-month-view-events/${entityId}`,
  GET_EVENT_COLORS_MINI_CALENDER: `${BASE_API_URL}/calendar-visualization/fetch-mini-calender-event-colors`,
  ADD_FOLLOWER: (eventRecordGUID: string) => `${BASE_API_URL}/calendar-events/participants/followers/add-follower/${eventRecordGUID}`,
  GET_EVENTS_FOR_SELECTED_CONTEXTS: (entityGUID) => `${BASE_API_URL}/calendar-visualization/selected-context-events/${entityGUID}`,
  GET_EVENTS_FOR_SELECTED_CONTEXTS_ON_SEARCH: (entityGUID) =>
    `${BASE_API_URL}/calendar-visualization/selected-context-events-search/${entityGUID}`,
};

export const CALENDAR_SHARE_URLS = {
  GET_EVENT_ASSOCIATED_RECORDS: `${BASE_API_URL}/calendar-events/share/event/associated-records`,
  GET_PARTICIPANT_ASSOCIATED_RECORDS: `${BASE_API_URL}/calendar-events/share/participants/associated-records`,
  GET_EVENT_SHARE_SETTINGS: `${BASE_API_URL}/calendar-events/share/event/get-settings`,
  GET_PARTICIPANT_SHARE_SETTINGS: `${BASE_API_URL}/calendar-events/share/participants/get-settings`,
  UPDATE_SHARE_SETTINGS: `${BASE_API_URL}/calendar-events/share/update-settings`,
  EVENT_SHARE: `${BASE_API_URL}/calendar-events/share`,
  PARTICIPANT_SHARE: `${BASE_API_URL}/calendar-events/share/participant`,
  ACCEPT_SHARE: (sharedInGUID: string) => `${BASE_API_URL}/calendar-events/share/shared-in/accept/${sharedInGUID}`,
  MATCH_SHARED_IN: (sharedInGUID: string) => `${BASE_API_URL}/calendar-events/share/shared-in/match/${sharedInGUID}`,
  IGNORE_SHARED_IN: (sharedInGUID: string) => `${BASE_API_URL}/calendar-events/shared-in/ignore/${sharedInGUID}`,
  HEED_SHARED_IN: (sharedInGUID: string) => `${BASE_API_URL}/calendar-events/shared-in/heed/${sharedInGUID}`,
  UPDATE_EVENT_COMPARATOR_DATA: (internalRecordGUID: string) => `${BASE_API_URL}/calendar-events/share/event/${internalRecordGUID}`,
  UPDATE_PARTICIPANT_COMPARATOR_DATA: (internalRecordGUID: string) =>
    `${BASE_API_URL}/calendar-events/share/participant/${internalRecordGUID}`,
  UPDATE_FOLLOWER_COMPARATOR_DATA: (internalRecordGUID: string) => `${BASE_API_URL}/calendar-events/share/follower/${internalRecordGUID}`,
  GET_MANUAL_OUT_CONTACTS_FOR_PARTICIPANTS: (eventId: string, role: string) =>
    `${BASE_API_URL}/calendar-events/share/manual-out-contacts/${eventId}?role=${role}`,
  GET_MANUAL_OUT_FIELDS: (eventId: string) => `${BASE_API_URL}/calendar-events/share/employer-settings/${eventId}`,
  TRASH_ACCEPT_SHARE: (trashRecordGUID: string) => `${BASE_API_URL}/calendar-events/trash/shared-in/accept/${trashRecordGUID}`,
  TRASH_IGNORE_SHARE: (trashRecordGUID: string) => `${BASE_API_URL}/calendar-events/trash/shared-in/ignore/${trashRecordGUID}`,
  TRASH_HEED_SHARE: (trashRecordGUID: string) => `${BASE_API_URL}/calendar-events/trash/shared-in/heed/${trashRecordGUID}`,
  TRASH_SHARE_ACTION_FROM_EVENT: (sharedInGUID: string, action: string) =>
    `${BASE_API_URL}/calendar-events/trash/shared-in/${action}/from-event/${sharedInGUID}`,
};

export const CONVERSATION_URLS = {
  GET_CONVERSATION_LIST: `${BASE_API_URL}/communication/conversations`,
  GET_CONVERSATION_DETAILS: (guid: string) => `${BASE_API_URL}/communication/conversations/${guid}`,
  CREATE_CHANNEL_RECORD: `${BASE_API_URL}/communication/conversations/create-channel`,
  CREATE_DIRECT_MESSAGE_CONVERSATION: `${BASE_API_URL}/communication/conversations/create-direct-message`,
  UPDATE_CONVERSATION_DETAILS: (conversationGUID: string) => `${BASE_API_URL}/communication/conversations/edit/${conversationGUID}`,
  DELETE_CONVERSATION: (entityGUID: string, guid: string) => `${BASE_API_URL}/communication/conversations/${entityGUID}/${guid}`,
  GET_ENTITIES_FOR_FILE_SHARE: `${BASE_API_URL}/communication/conversations/get-entities-for-record-share`,
  GET_RECORDS_FOR_MENTIONS: (entityGUID: string, module?: MessageRecordModuleType) =>
    `${BASE_API_URL}/communication/conversations/${entityGUID}/browse-records?module=${module || ''}`,
  GET_MEMBER_CONNECTIONS_FROM_INTERNAL_CONTACTS: (contactGUID: string) =>
    `${BASE_API_URL}/communication/conversations/connection-options/${contactGUID}`,
  GET_CONVERSATIONS_LITE: (guid: string) => `${BASE_API_URL}/communication/conversations/conversations-lite/${guid}`,
};

export const CONVERSATION_MEMBERS_URLS = {
  GET_PERSON_MENTIONS_LIST: (contextGUID: string, conversationGUID?: string) =>
    `${BASE_API_URL}/communication/conversations/get-person-mentions-list/${contextGUID}?conversationGUID=${conversationGUID || ''}`,
};

export const MESSAGES_URLS = {
  FETCH_MESSAGES_LIST: `${BASE_API_URL}/communication/messages/fetch-messages-list-paginate`,
  GET_MESSAGE_BY_GUID: (guid: string) => `${BASE_API_URL}/communication/messages/${guid}`,
  SEND_MESSAGE: `${BASE_API_URL}/communication/messages/send-message`,
  DELETE_MESSAGE: (entityGUID: string, guid: string) => `${BASE_API_URL}/communication/messages/${entityGUID}/${guid}`,
  FORWARD_MESSAGE: `${BASE_API_URL}/communication/messages/forward-message`,
  REACT_TO_MESSAGE: (guid: string) => `${BASE_API_URL}/communication/messages/${guid}/react`,
};

export const PREFERENCE_URLS = {
  GET_PREFERENCE_LIST: `${BASE_API_URL}/communication/conversations/get-preferences`,
  GET_PREFERENCE_DETAILS: (guid: string) => `${BASE_API_URL}/communication/conversations/preference-record/${guid}`,
  GET_UNREVIEWED_MESSAGES: (entityGUID: string, conversationGUID: string) =>
    `${BASE_API_URL}/communication/conversations/${entityGUID}/${conversationGUID}/unseen-messages`,
  UPDATE_PREFERENCES: (memberGUID: string) => `${BASE_API_URL}/communication/conversations/${memberGUID}/preferences`,
};

export const CHAT_SHARE_URLS = {
  GET_SHARED_OUT_LIST: `${BASE_API_URL}/communication/shared-out/sharedOut-record-list-paginate`,
  SHARED_OUT_RECORD_BY_GUID: (sharedOutGUID: string) => `${BASE_API_URL}/communication/shared-out/${sharedOutGUID}`,
  GET_SHARED_IN_LIST: `${BASE_API_URL}/communication/shared-in/sharedIn-record-list-paginate`,
  SHARED_IN_RECORD_BY_GUID: (sharedInGUID: string) => `${BASE_API_URL}/communication/shared-in/${sharedInGUID}`,
  DELETE_SHARED_IN_RECORD: (guid: string) => `${BASE_API_URL}/communication/shared-in/${guid}`,
  DELETE_SHARED_OUT_RECORD: (guid: string) => `${BASE_API_URL}/communication/shared-out/${guid}`,
  ACCEPT_SHARE: `${BASE_API_URL}/communication/shared-in/accept`,
  IGNORE_SHARE: `${BASE_API_URL}/communication/shared-in/ignore`,
  GET_SHARED_RECORDS_BY_RECORD_GUID: `${BASE_API_URL}/communication/shared-out/all-shared-records`,
};
export const COMMUNICATION_URLS = {
  GET_DELETED_RECORDS: `${BASE_API_URL}/communication/trash/deleted-list-paginate`,
  GET_DELETED_RECORD_BY_GUID: (guid: string) => `${BASE_API_URL}/communication/trash/deleted-record/${guid}`,
};

export const CHAT_TRASH_URLS = {
  GET_TRASH_LIST: (entityId: string) => `${BASE_API_URL}/communication/trash/list-paginate/${entityId}`,
  GET_TRASH_RECORD_BY_GUID: (recordGUID: string) => `${BASE_API_URL}/communication/trash/${recordGUID}`,
  RECOVER_RECORD: (recordGUID: string) => `${BASE_API_URL}/communication/trash/recover-record/${recordGUID}`,
  ACCEPT_SHARE: (guid: UUID) => `${BASE_API_URL}/communication/trash/shared-in/accept/${guid}`,
  IGNORE_SHARE: (guid: UUID) => `${BASE_API_URL}/communication/trash/shared-in/ignore/${guid}`,
};

export const COMPANY_CONFIGURATION_URLS = {
  GET_SITE_LOCATIONS: (recordOfGUID: string, dimensionGUID: string) => `${BASE_API_URL}/site-locations/${recordOfGUID}/${dimensionGUID}`,
  CREATE_SITE_LOCATION: `${BASE_API_URL}/site-locations/create-site-profile`,
  GET_ALL_POSTAL_ADDRESS_FOR_ENTITY: (recordOfGUID: string) => `${BASE_API_URL}/contacts/address/${recordOfGUID}`,
  GET_ALL_SITE_LOCATIONS_FOR_ENTITY: (recordOfGUID: string) => `${BASE_API_URL}/contacts/records/${recordOfGUID}`,
  UPDATE_SITE_LOCATION: (guid: string) => `${BASE_API_URL}/site-locations/${guid}`,
  GET_SITES_DETAILS: (recordOfGUID: string, contactGUID: string) => `${BASE_API_URL}/site-locations/${recordOfGUID}/${contactGUID}`,

  CREATE_DIMENSION: `${BASE_API_URL}/company-configurations/dimensions`,
  GET_DIMENSIONS: `${BASE_API_URL}/company-configurations/dimensions/list`,
  GET_DIMENSION_BY_ID: (dimensionGUID: string) => `${BASE_API_URL}/company-configurations/dimensions/${dimensionGUID}`,
  UPDATE_DIMENSION: (dimensionGUID: string) => `${BASE_API_URL}/company-configurations/dimensions/${dimensionGUID}`,
  DEACTIVATE_DIMENSION: (dimensionGUID: string) => `${BASE_API_URL}/company-configurations/dimensions/${dimensionGUID}/deactivate`,
  GET_JOB_TITLE_DETAILS: (guid: string) => `${BASE_API_URL}/company-configurations/job-titles/${guid}`,
};
